<template>
    <Modal
        v-if="isRatingValid"
        modal-name="ratingModal"
        size="lg"
        :title="showRatingLink ? $t('reviewModal.thankYouForRating') : $t('reviewModal.title')"
    >
        <template v-if="!feedbackSubmitFailed">
            <div v-if="!loading && (isRatingSent || isAlreadyRated)">
                <div v-if="showRatingLink">
                    <p>{{ $t('reviewModal.googleRating') }}</p>
                    <a :href="pharmacyRatingUrl || ''" class="btn btn-primary fs-13 mt-3" type="submit" target="_blank">
                        {{ $t('reviewModal.googleCta') }}
                    </a>
                </div>
                <template v-else>
                    <img
                        v-lazy-load
                        src="/nuxt-assets/images/lazy-load-error.svg"
                        data-src="/nuxt-assets/rating/smile10.png"
                        alt=""
                        class="mx-auto my-3 d-block"
                    />
                    <div class="fs-24 text-center">
                        {{ $t(isAlreadyRated ? 'reviewModal.alreadyRated' : 'reviewModal.thankYouForRating') }}
                    </div>
                </template>
            </div>
            <div v-else id="js-pilulka-service-rating-modal-body" class="modal-body">
                <div class="alert alert-success">
                    {{ $t('reviewModal.thankYouNote') }}
                </div>

                <article class="pt-4 pb-5">
                    <div class="text-orange pl-2 pb-2 d-md-none">{{ $t('reviewModal.completelyUnlikely') }}</div>

                    <div class="row flex-md-nowrap justify-content-center text-gray">
                        <div v-for="i in 10" :key="i" class="position-relative col-20 col-md d-flex align-items-center my-2 px-2">
                            <input
                                :id="`rating${i}`"
                                v-model="rating"
                                type="radio"
                                name="rating"
                                :value="i"
                                class="user-feedback__radio d-none"
                            />
                            <label
                                :for="`rating${i}`"
                                class="fs-16 fwb col-12 col-md-11 px-0 text-center text-rating1 cursor-point mb-0 d-flex align-items-center"
                            >
                                <img
                                    v-lazy-load
                                    :alt="`${i}`"
                                    class="px-2 d-block user-feedback__smile"
                                    src="/nuxt-assets/images/lazy-load-error.svg"
                                    :data-src="`/nuxt-assets/rating/smile${i}.png`"
                                />
                            </label>
                        </div>
                    </div>

                    <div class="px-3 pt-1">
                        <span class="text-orange d-none d-md-inline">{{ $t('reviewModal.completelyUnlikely') }}</span>
                        <span class="text-success float-right">{{ $t('reviewModal.completelyLikely') }}</span>
                    </div>
                </article>

                <textarea
                    v-model="text"
                    class="form-control resize-vertical"
                    name="text"
                    rows="4"
                    :placeholder="$t('reviewModal.ratingText')"
                />

                <div class="d-flex justify-content-end align-items-center">
                    <a
                        v-if="$t('reviewModal.formLink')"
                        id="survey-link"
                        :href="$t('reviewModal.formLink')"
                        target="_blank"
                        class="mt-3 fs-13 mr-3"
                    >
                        {{ $t('reviewModal.feedbackForm') }}
                    </a>
                    <button class="btn btn-primary fs-13 mt-3" type="submit" :disabled="isRatingSent" @click="sendFeedback(true)">
                        {{ $t('reviewModal.sendRating') }}
                        <i class="arrow arrow--right" />
                    </button>
                </div>
            </div>
        </template>
        <div v-else class="alert alert-danger">
            {{ $t('somethingWentWrong') }}
        </div>
        <Loader v-if="loading" />
    </Modal>
</template>

<script lang="ts">
import Modal from '~/components/Modal.vue'
import Loader from '~/components/ui/Loader.vue'
import { useAuthStore } from '~/stores/auth'

export default {
    components: { Modal, Loader },

    props: {
        uid: {
            type: String,
            default: '',
        },
        feedbackHash: {
            type: String,
            default: '',
        },
        objectId: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: '',
        },
        emailRating: {
            type: Number,
            default: 1,
        },
    },

    data() {
        return {
            loading: false,
            text: '',
            isRatingSent: false,
            isAlreadyRated: false,
            feedbackSubmitFailed: false,
            rating: this.emailRating,
            pharmacyRatingUrl: null,
        }
    },

    computed: {
        isRatingValid(): boolean {
            return this.uid && this.feedbackHash && this.objectId && this.type
        },

        showRatingLink(): boolean {
            return (
                !this.feedbackSubmitFailed &&
                (this.isRatingSent || this.isAlreadyRated) &&
                this.pharmacyRatingUrl !== null &&
                this.rating > 6
            )
        },
    },

    mounted() {
        if (this.isRatingValid) {
            this.sendFeedback(false)
            useNuxtApp().$bus.$emit('showModal', 'ratingModal')
        }
    },

    methods: {
        async sendFeedback(markAsSent = true): Promise<void> {
            try {
                this.loading = true
                const { rating, type, objectId, feedbackHash, text, uid } = this
                const authStore = useAuthStore()
                const accessToken = authStore.accessToken
                this.isRatingSent = markAsSent

                const { isAlreadyRated, pharmacyRatingUrl } = await this.$repository.sendFeedback({
                    accessToken,
                    rating,
                    type,
                    objectId,
                    feedbackHash,
                    uid,
                    text,
                })

                this.isAlreadyRated = isAlreadyRated
                this.pharmacyRatingUrl = pharmacyRatingUrl
                this.loading = false
            } catch {
                this.feedbackSubmitFailed = true
                this.loading = false
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.user-feedback {
    &__radio {
        & + label {
            transition: 0.25s transform, background;

            .rating-title {
                display: none;
            }
        }

        &:checked + label {
            transform: scale(1.2);

            img {
                background: rgba(255, 192, 203, 0.2);
                padding: 0.5rem 0;
                border-radius: 50%;
            }

            .rating-title {
                display: inline;
            }
        }
    }

    &__smile {
        max-width: 55px;
        margin: auto;
        width: 100%;
    }
}
</style>
